import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { Button, SaltIcons, useDensity } from "@/design-system/salt";

/**
 * DR-33 — at mobile density the app shows ONE tab row: the CURRENT level. The level above
 * becomes a back affordance, not a second visible row.
 *
 * MEASURED 2026-08-18 at 390x844 with a coarse pointer (`--salt-size-base: 44px`,
 * `salt-density-mobile`) on `/corporate/440/campaigns`: THREE stacked tab rows at y=157,
 * y=241 and y=325, 60px each, pushing the first content card to y=523 of an 844px screen.
 * Evidence: `docs/audit/chrome-rebuild/03-dr33-one-tab-row.md`.
 *
 * WHY A REGISTRY AND NOT A PROP — the three rows are rendered by three different components
 * (`CorporateTabNavigation`, a page-level `ParentTabBar`, and `TabsList`) from three
 * different files, and no one of them knows the others exist. 32+ pages compose them
 * freely. A prop would have to be threaded through every one of those pages and would be
 * wrong the moment a page added or removed a level. The rows discover their own depth
 * instead, from the DOM they already produce.
 *
 * WHY DENSITY AND NOT `useIsAtLeast("sm")` — `useDensity()` resolves to `"mobile"` only
 * when `SaltFoundationProvider` sees a coarse pointer AND a narrow viewport
 * (`SaltFoundationProvider.tsx:83`), which is this app's single definition of "phone".
 * `useIsAtLeast` is viewport-only, so it would also collapse the rows in a narrow DESKTOP
 * window, where all three rows fit and DR-33 says nothing should change.
 */

interface NavigationLevelContextValue {
  /** Attach to a tab row's outermost element. `null` on unmount. */
  registerLevel: (id: string, element: HTMLElement | null) => void;
  /** Rows not at the visible depth stay mounted but are removed from layout and a11y. */
  isLevelHidden: (id: string) => boolean;
}

const NavigationLevelContext = createContext<NavigationLevelContextValue | null>(null);

interface BackTarget {
  label: string;
  goUp: () => void;
}

const BackTargetContext = createContext<BackTarget | null>(null);

/** Reads the active tab's visible text out of a registered row. */
function activeLabelOf(element: HTMLElement): string | null {
  const selected = element.querySelector<HTMLElement>('[aria-selected="true"]');
  const text = selected?.textContent?.trim();
  return text ? text : null;
}

let levelSeq = 0;

/** A stable id for one tab row instance, for the lifetime of that instance. */
export function useNavigationLevelId(): string {
  const ref = useRef<string>();
  if (!ref.current) {
    levelSeq += 1;
    ref.current = `nav-level-${levelSeq}`;
  }
  return ref.current;
}

export function NavigationLevelProvider({ children }: { children: React.ReactNode }) {
  const density = useDensity();
  const collapse = density === "mobile";
  const { pathname } = useLocation();

  const elements = useRef(new Map<string, HTMLElement>());
  const [revision, setRevision] = useState(0);
  const [order, setOrder] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [drill, setDrill] = useState(0);

  const registerLevel = useCallback((id: string, element: HTMLElement | null) => {
    if (element) elements.current.set(id, element);
    else elements.current.delete(id);
    setRevision((value) => value + 1);
  }, []);

  // DOM order, not mount order. React runs sibling effects in tree order today, but that is
  // an implementation detail and these rows are siblings only by convention — one of them
  // arrives wrapped in `CorporateEmailNavigation`. `compareDocumentPosition` asks the
  // document what the reader actually sees, which is the thing the rule is about.
  useLayoutEffect(() => {
    const live = [...elements.current.entries()].filter(([, element]) =>
      element.isConnected,
    );
    live.sort(([, a], [, b]) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    );
    const nextOrder = live.map(([id]) => id);
    setOrder((previous) =>
      previous.length === nextOrder.length && previous.every((id, i) => id === nextOrder[i])
        ? previous
        : nextOrder,
    );
    const nextLabels: Record<string, string> = {};
    for (const [id, element] of live) {
      const label = activeLabelOf(element);
      if (label) nextLabels[id] = label;
    }
    setLabels((previous) => {
      const keys = Object.keys(nextLabels);
      const same =
        keys.length === Object.keys(previous).length &&
        keys.every((key) => previous[key] === nextLabels[key]);
      return same ? previous : nextLabels;
    });
  }, [revision, pathname, collapse]);

  // A drill-out is a view state, not a location. Landing on a new route must start at the
  // deepest level again, or the user arrives already collapsed out of the row they came for.
  useEffect(() => setDrill(0), [pathname]);

  const visibleIndex = Math.max(0, order.length - 1 - Math.min(drill, order.length - 1));

  const isLevelHidden = useCallback(
    (id: string) => {
      if (!collapse || order.length < 2) return false;
      const index = order.indexOf(id);
      return index !== -1 && index !== visibleIndex;
    },
    [collapse, order, visibleIndex],
  );

  const value = useMemo<NavigationLevelContextValue>(
    () => ({ registerLevel, isLevelHidden }),
    [registerLevel, isLevelHidden],
  );

  const parentId = visibleIndex > 0 ? order[visibleIndex - 1] : undefined;
  const backTarget = useMemo<BackTarget | null>(() => {
    if (!collapse || !parentId) return null;
    return {
      label: labels[parentId] ?? "Back",
      goUp: () => setDrill((current) => current + 1),
    };
  }, [collapse, parentId, labels]);

  return (
    <NavigationLevelContext.Provider value={value}>
      <BackTargetContext.Provider value={backTarget}>{children}</BackTargetContext.Provider>
    </NavigationLevelContext.Provider>
  );
}

/**
 * For a tab row. Returns `null` when the row should render exactly as it always has —
 * outside a provider, or at any density other than mobile. Desktop therefore runs the
 * ORIGINAL element tree, not a wrapped one, which is what makes the no-op provable rather
 * than hoped for.
 */
export function useNavigationLevelSlot(id: string): {
  ref: (element: HTMLElement | null) => void;
  hidden: boolean;
} | null {
  const context = useContext(NavigationLevelContext);
  const density = useDensity();
  const { registerLevel, isLevelHidden } = context ?? {};

  const ref = useCallback(
    (element: HTMLElement | null) => registerLevel?.(id, element),
    [registerLevel, id],
  );

  useEffect(() => () => registerLevel?.(id, null), [registerLevel, id]);

  if (!context || density !== "mobile") return null;
  return { ref, hidden: isLevelHidden!(id) };
}

/**
 * The back affordance. A REAL control with an accessible name, per DR-33 — not a decorative
 * chevron. Renders nothing when there is no level above the visible one, which is every
 * desktop render and every single-row route.
 */
export function ParentLevelBackControl() {
  const target = useContext(BackTargetContext);
  if (!target) return null;

  return (
    <Button
      appearance="transparent"
      sentiment="neutral"
      onClick={target.goUp}
      aria-label={`Back to ${target.label}`}
      style={{
        alignSelf: "flex-start",
        minHeight: "var(--salt-size-base)",
        gap: "var(--salt-spacing-50)",
      }}
    >
      <SaltIcons.ChevronLeftIcon aria-hidden />
      <span>{target.label}</span>
    </Button>
  );
}
